// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { useEffect, useMemo, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import qrcode from 'qrcode-generator';
import {
  api,
  isOfferUnavailable,
  type OfferResponse,
  type CreateVaultResponse,
  type RegisterVaultPolicyRequest,
} from '../api.js';
import { useSession } from '../session-context.js';
// Type-only (erased at build): the entry chunk stays device-code-free.
import type { KeyAgentClient } from '../key-agent-worker-client.js';
import type { HardwareKeyAgentClient } from '../hardware-agent-client.js';
import type { VaultPolicyRegistration } from '@cobault/key-agent/ledger-agent';
import {
  expectedRewardSats,
  formatBtc,
  formatMaturityShort,
  formatPercent,
  formatUsd,
  parseBtcToSats,
  truncate,
} from '../format.js';
import { rememberVaultDepositAddress, saveVaultMapping } from '../vault-store.js';
import { sealVaultAnchorAfterAcknowledgement } from '../vault-anchor.js';
import { ProductSubtitle } from '../components/product-subtitle.js';
import { resolveBrand } from '../brand.js';
import { BrandLockup } from '../components/brand-lockup.js';
import { mempoolAddressUrlForNetwork } from '../components/deposit-address-field.js';
import { verifierOriginForNetwork } from '../verifier-embed.js';
import { VerifierConfirmModal } from '../components/verifier-confirm-modal.js';
import '../styles/accept.css';

type Step =
  | 'amount'
  | 'payout'
  | 'confirm'
  | 'creating'
  // HW.4: hardware sessions register the vault's wallet policy on-device
  // between creation and success; a declined/failed ceremony parks on
  // 'register-failed', whose retry re-runs ONLY the ceremony (the vault
  // already exists server-side).
  | 'registering'
  | 'register-failed'
  | 'success';
type PayoutChoice = 'login-wallet' | 'custom';

/** The wizard proper, in order. Position in here is the single source of
 *  both the "Step N of M" label and the dot row; the states after it
 *  ('creating', 'registering', …) are outcomes, not steps, and render no
 *  indicator. */
const WIZARD_STEPS: readonly Step[] = ['amount', 'payout', 'confirm'];

/** Choose the payout address for a new vault. Priority:
 *    1. `customPasted` — user explicitly entered one in Step 2.
 *    2. `walletAddress` — the personal wallet address shown on /wallet;
 *       settled funds land where the Wallet page's Send can spend them.
 *    3. `refundAddress` — per-vault refund-path address. Last-resort
 *       fallback if wallet-address derivation failed; still recoverable
 *       from the same seed but not surfaced anywhere else in the UI.
 *  Exported for unit tests. */
export function pickPayoutAddress(
  customPasted: string,
  walletAddress: string | null,
  refundAddress: string,
): string {
  const trimmed = customPasted.trim();
  if (trimmed.length > 0) return trimmed;
  if (walletAddress) return walletAddress;
  return refundAddress;
}

/** HW.4: a hardware session's agent carries the registration-ceremony
 *  capability; its presence IS the "device session" signal here — no
 *  extra session state to thread. Software/Web3Auth agents return null.
 *  Exported for unit tests. */
export function policyRegistrar(
  agent: KeyAgentClient | null,
): HardwareKeyAgentClient | null {
  if (!agent) return null;
  const candidate = agent as KeyAgentClient & Partial<HardwareKeyAgentClient>;
  return typeof candidate.registerVaultPolicy === 'function'
    ? (candidate as HardwareKeyAgentClient)
    : null;
}

/** COB-336: did a device call fail because the device is gone / unresponsive
 *  (vs a genuine on-device decline)? These are the two typed errors the
 *  key-agent guard raises when the transport hangs, is wedged, or disconnects
 *  — their retry MUST reconnect a fresh device first (the old transport is
 *  dead). Discriminated by name so the device chunk is never imported here.
 *  Exported for unit tests. */
export function isDeviceLostError(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name ?? '';
  return name === 'DeviceUnresponsiveError' || name === 'DeviceDisconnectedError';
}

/** HW.4: map a registration-ceremony failure to actionable copy. Error
 *  discrimination is by name/message (the transport error classes live in
 *  the lazy device chunk — never imported here). Exported for unit tests. */
export function registrationErrorMessage(err: unknown): string {
  const e = err as { name?: string; message?: string } | null;
  const name = e?.name ?? '';
  const message = e?.message ?? '';
  if (name === 'TransportStatusError' || /0x6985|denied|rejected/i.test(message)) {
    return (
      'The registration was declined on the device. Approve the vault ' +
      'policy on your Ledger to finish setting up this vault.'
    );
  }
  // COB-336: the guard's typed errors carry their own actionable
  // "reconnect/unlock" copy — surface it verbatim.
  if (name === 'DeviceUnresponsiveError' || name === 'DeviceDisconnectedError') {
    return (
      message ||
      'Your Ledger appears locked or disconnected. Reconnect and unlock it, then try again.'
    );
  }
  if (name.startsWith('DisconnectedDevice')) {
    return 'Your Ledger was disconnected. Reconnect and unlock it, then try again.';
  }
  return message || 'Device registration failed. Check your Ledger and try again.';
}

/** HW.9 (COB-329): shape the ceremony result into the registration-record
 *  POST body. `record.keys` is `[U, P, V, U_refund]` in template order
 *  (ledger-agent `vaultPolicyTemplate`); the server records only the two
 *  keys the device controls — P/V are written server-side at creation.
 *  Exported for unit tests. */
export function registrationRequestBody(
  reg: VaultPolicyRegistration,
): RegisterVaultPolicyRequest {
  return {
    user_key: reg.record.keys[0],
    user_refund_key: reg.record.keys[3],
    derivation_index: reg.derivationIndex,
  };
}

export function Accept() {
  const { route } = useLocation();
  const session = useSession();

  // Redirect to home if not logged in or KYC not yet passed (F3).
  useEffect(() => {
    if (!session.isLoggedIn) {
      route('/');
      return;
    }
    if (session.isAuthenticated && session.kycStatus !== null
        && session.kycStatus !== 'passed') {
      route('/');
    }
  }, [session.isLoggedIn, session.isAuthenticated, session.kycStatus, route]);

  const [step, setStep] = useState<Step>('amount');
  const [error, setError] = useState<string | null>(null);
  const [offer, setOffer] = useState<OfferResponse | null>(null);
  const [offerError, setOfferError] = useState<string | null>(null);
  // COB-149: default the deposit to 0.1 BTC — the canonical vault size
  // (deposits are pinned to 0.1-BTC multiples), shown clean with no trailing
  // decimals rather than an empty field.
  const [btcInput, setBtcInput] = useState('0.1');
  // Step 2. Defaults to the Cobault login wallet, which the Step 3 confirm
  // card names explicitly so the destination is never silent.
  const [payoutChoice, setPayoutChoice] = useState<PayoutChoice>('login-wallet');
  const [customPayoutAddress, setCustomPayoutAddress] = useState('');
  const [vaultResponse, setVaultResponse] = useState<CreateVaultResponse | null>(null);
  // HW.4: the created-but-unregistered vault a hardware session's retry
  // re-runs the ceremony for. Never triggers a second POST /vault.
  const [pendingRegistration, setPendingRegistration] = useState<{
    response: CreateVaultResponse;
    derivationIndex: number;
  } | null>(null);
  // COB-336: the last register-ceremony failure was a lost/unresponsive
  // device (its transport is dead), so the retry must reconnect a fresh
  // device before re-running the ceremony. Drives the retry button's label.
  const [needsReconnect, setNeedsReconnect] = useState(false);
  // Personal wallet address — the address shown on /wallet. Used as the
  // default payout target when the user doesn't paste a custom one, so
  // settled funds land where the Wallet page's "Send" can spend them.
  // Falls back to the vault's per-index refund-path address only if
  // derivation failed (both are still recoverable from the same seed).
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  // COB-462: the payout address this browser actually chose for the vault
  // (`pickPayoutAddress`), held so the acknowledgement-time anchor seal pins
  // the client's choice rather than reading it back off a server response.
  const [sealPayoutAddress, setSealPayoutAddress] = useState<string | null>(null);

  useEffect(() => {
    // F1: /offer is now protected — only fetch once authenticated.
    if (!session.isAuthenticated) return;
    api.getOffer()
      .then((res) => {
        // COB-386: the accept flow needs a live offer to build a vault. A
        // legitimate "no offer right now" state (thin market / maintenance)
        // is surfaced as a friendly go-back message rather than a broken
        // form; the raw reason/term stays out of the copy.
        if (isOfferUnavailable(res)) {
          setOfferError(
            res.reason === 'maintenance'
              ? 'Yield offers are paused briefly for maintenance. Please go back and try again shortly.'
              : 'No yield offer is available right now. Please go back and try again in a moment.',
          );
        } else {
          setOffer(res);
        }
      })
      .catch(() => setOfferError('Offer unavailable. Please go back and try again.'));
  }, [session.isAuthenticated]);

  useEffect(() => {
    if (!session.agent || !session.isAuthenticated) return;
    let cancelled = false;
    session.agent.getWalletAddress()
      .then((addr) => { if (!cancelled) setWalletAddress(addr); })
      .catch(() => { /* Leave null — handleCreateVault falls back gracefully. */ });
    return () => { cancelled = true; };
  }, [session.agent, session.isAuthenticated]);

  const principalSats = parseBtcToSats(btcInput);
  const validPrincipal = isFinite(principalSats) && principalSats > 0;
  // Floor so float imprecision never lets us round up past the cap —
  // trading rejects `amount_btc > offer_limit_btc` at /trade/execute
  // and the watcher loop can't recover the user cleanly from that.
  const maxPrincipalSats = offer ? Math.floor(offer.offer_limit_btc * 100_000_000) : 0;
  const exceedsLimit = validPrincipal && offer !== null && principalSats > maxPrincipalSats;
  const canSubmitAmount = validPrincipal && !exceedsLimit;
  // F16: reward = yield_apy × days/365 × principal — same formula the
  // backend uses to lock `reward_sats` into the contract.
  const estimatedRewardSats = offer && canSubmitAmount
    ? expectedRewardSats(
        principalSats,
        offer.economics.yield_apy,
        offer.days_to_expiry,
      )
    : 0;

  // Step 2: Continue is enabled when login-wallet is selected OR a non-empty
  // custom address is typed. Backend validates the address shape on POST
  // /vault; the SPA only enforces "not blank".
  const canSubmitPayout = payoutChoice === 'login-wallet'
    || (payoutChoice === 'custom' && customPayoutAddress.trim().length > 0);

  const stepNumber = WIZARD_STEPS.indexOf(step) + 1;

  function goNext() {
    if (step === 'amount' && canSubmitAmount) {
      setStep('payout');
    } else if (step === 'payout' && canSubmitPayout) {
      setStep('confirm');
    }
  }

  function goBack() {
    if (step === 'amount') {
      route('/');
    } else if (step === 'payout') {
      setStep('amount');
    } else if (step === 'confirm') {
      setStep('payout');
    }
  }

  async function handleCreateVault() {
    setError(null);
    if (!canSubmitAmount) {
      setError('Enter a valid BTC amount greater than zero.');
      setStep('amount');
      return;
    }
    if (exceedsLimit && offer) {
      setError(
        `Amount exceeds the current offer capacity of ${offer.offer_limit_btc.toFixed(4)} BTC.`,
      );
      setStep('amount');
      return;
    }
    if (!session.agent) {
      setError('Session expired. Please go back and unlock again.');
      return;
    }

    setStep('creating');
    try {
      const vaultId = crypto.randomUUID();
      const keys = await session.agent.generateKey(vaultId);

      // WS-D (COB-285): export U / U_refund as account key-origins so the
      // server can re-derive each at its pinned index and byte-compare
      // against the literal descriptor (the creation-time equality gate).
      // Same seed and index as `generateKey` above, so this never fails a
      // creation that generateKey succeeded for.
      const accountXpubs = await session.agent.exportAccountXpubs(vaultId, keys.derivationIndex);
      const toKeyOrigin = (r: { masterFingerprint: string; accountPath: string; xpub: string }) =>
        `[${r.masterFingerprint}/${r.accountPath.replace(/^m\//, '')}]${r.xpub}`;

      const customForPicker = payoutChoice === 'custom' ? customPayoutAddress : '';
      const effectivePayoutAddress = pickPayoutAddress(
        customForPicker,
        walletAddress,
        keys.refundAddress,
      );
      const response = await api.createVault({
        user_multisig_pubkey_hex: keys.pubkey,
        user_refund_pubkey_hex: keys.refundPubkey,
        user_payout_address: effectivePayoutAddress,
        principal_sats: principalSats,
        user_fingerprint: session.fingerprint!,
        derivation_index: keys.derivationIndex,
        user_key: toKeyOrigin(accountXpubs.user),
        user_refund_key: toKeyOrigin(accountXpubs.userRefund),
      });

      saveVaultMapping(response.vault_id, keys.derivationIndex, session.fingerprint!, {
        state: response.state,
        principalSats,
        // COB-245: always absent at creation. `rememberVaultDepositAddress`
        // fills it in at acknowledgement, when the address is revealed.
        depositAddress: response.deposit_address,
        createdAtUnix: Math.floor(Date.now() / 1000),
      });

      // COB-462: the COB-178 anchor is NOT sealed here. Since COB-245 the
      // deposit address is withheld until the owner confirms the registered
      // contract on the independent verifier, so at this point the triple is
      // incomplete and there is nothing to seal. The seal moved to
      // acknowledgement — `SuccessStep.handleAcknowledged` below, and the
      // matching pass in `vault-detail.tsx` for the vault-page arm. The payout
      // address the browser chose is carried there in state so the anchor pins
      // the client's choice, never a server echo of it.
      setSealPayoutAddress(effectivePayoutAddress);

      // HW.4: a hardware session registers the vault's wallet policy
      // on-device before the success screen — the ceremony the device's
      // WYSIWYS signing model is rooted in (signPsbt re-presents the
      // registered policy + HMAC on every spend). A failed/declined
      // ceremony does NOT undo the vault: it parks on 'register-failed'
      // and the retry re-runs only the ceremony.
      if (policyRegistrar(session.agent)) {
        setPendingRegistration({ response, derivationIndex: keys.derivationIndex });
        await finishWithRegistration(response, keys.derivationIndex);
        return;
      }

      setVaultResponse(response);
      setStep('success');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Vault creation failed.';
      setError(msg);
      setStep('confirm');
    }
  }

  /** HW.4: drive the on-device ceremony for an already-created vault,
   *  then land on success — or park on 'register-failed' with a retry.
   *  COB-336: `agentOverride` lets the retry hand in a freshly reconnected
   *  agent (the swap into session state is async, so the click can't rely on
   *  `session.agent` yet). A lost-device failure sets `needsReconnect` so the
   *  next retry reconnects first. */
  async function finishWithRegistration(
    response: CreateVaultResponse,
    derivationIndex: number,
    agentOverride?: KeyAgentClient,
  ): Promise<void> {
    setError(null);
    setStep('registering');
    try {
      const registrar = policyRegistrar(agentOverride ?? session.agent);
      if (!registrar) {
        throw new Error(
          'Your hardware session has ended — sign in with your device again ' +
            'to finish registering this vault.',
        );
      }
      // HW.9 (COB-329): the real platform (P) + verifier (V) account
      // xpubs the server provisioned, carried on the create response in
      // the device app's version bytes. Absent only for a non-enclave
      // (kms_resident) cohort, which the hardware device flow cannot
      // build a 2-of-3 policy for.
      const cosigners = response.cosigner_account_keys;
      if (!cosigners) {
        throw new Error(
          'This vault was provisioned without hardware-compatible co-signer ' +
            'keys, so the device policy cannot be registered. Contact support.',
        );
      }
      const registration = await registrar.registerVaultPolicy(
        response.vault_id,
        {
          platformKey: cosigners.platform,
          verifierKey: cosigners.verifier,
          refundUnlockBlock: response.contract.refund_unlock_block,
        },
        derivationIndex,
      );
      // HW.9: strict — a failed record POST parks on 'register-failed' and
      // the retry re-runs the whole ceremony (the on-device HMAC and the
      // server upsert are both idempotent), rather than being swallowed.
      await api.registerVaultPolicy(
        response.vault_id,
        registrationRequestBody(registration),
      );
    } catch (e) {
      setError(registrationErrorMessage(e));
      // COB-336: a lost/unresponsive device wedges its transport — the retry
      // must reconnect a fresh one. A plain decline reuses the live agent.
      setNeedsReconnect(isDeviceLostError(e));
      setStep('register-failed');
      return;
    }
    setNeedsReconnect(false);
    setVaultResponse(response);
    setStep('success');
  }

  /** COB-336: the 'register-failed' retry. When the failure lost the device,
   *  reconnect a fresh one (in this click's user gesture) and re-run the
   *  ceremony against it; otherwise re-run against the live agent. */
  async function retryRegistration(): Promise<void> {
    if (!pendingRegistration) return;
    const { response, derivationIndex } = pendingRegistration;
    if (!needsReconnect) {
      await finishWithRegistration(response, derivationIndex);
      return;
    }
    setError(null);
    setStep('registering');
    let fresh: KeyAgentClient;
    try {
      fresh = await session.reconnectHardwareAgent();
    } catch (e) {
      // Wrong device, cancelled chooser, or still-unresponsive — stay on the
      // retry screen (still needing a reconnect) with an actionable message.
      setError(registrationErrorMessage(e));
      setNeedsReconnect(true);
      setStep('register-failed');
      return;
    }
    setNeedsReconnect(false);
    await finishWithRegistration(response, derivationIndex, fresh);
  }

  if (!session.isLoggedIn) return null;

  if (offerError) {
    return (
      <div class="accept-page">
        <p class="accept-error">{offerError}</p>
        <div class="accept-actions">
          <a href="/" class="btn btn-secondary">Back to home</a>
        </div>
      </div>
    );
  }

  if (!offer) {
    return <div class="accept-page"><p class="accept-loading">Loading offer...</p></div>;
  }

  // COB-394: sibling-brand treatment for the offer's counterparty, scoping
  // the whole accept wizard. Inert today — `offer.productco` is absent so
  // `brand.attr` is undefined and the `data-brand` attribute is omitted;
  // the loading/error shells above stay neutral by design (no offer, no
  // descriptor). The DOM here is identical to pre-COB-394.
  const brand = resolveBrand(offer.productco);

  return (
    <div class="accept-page" data-brand={brand.attr}>
      {stepNumber > 0 && <StepIndicator current={stepNumber} />}

      {step === 'amount' && (
        <AmountStep
          offer={offer}
          btcInput={btcInput}
          setBtcInput={setBtcInput}
          exceedsLimit={exceedsLimit}
          canSubmit={canSubmitAmount}
          estimatedRewardSats={estimatedRewardSats}
          onBack={goBack}
          onNext={goNext}
        />
      )}

      {step === 'payout' && (
        <PayoutStep
          payoutChoice={payoutChoice}
          setPayoutChoice={setPayoutChoice}
          walletAddress={walletAddress}
          customPayoutAddress={customPayoutAddress}
          setCustomPayoutAddress={setCustomPayoutAddress}
          canSubmit={canSubmitPayout}
          onBack={goBack}
          onNext={goNext}
        />
      )}

      {step === 'confirm' && (
        <ConfirmStep
          offer={offer}
          principalSats={principalSats}
          estimatedRewardSats={estimatedRewardSats}
          payoutChoice={payoutChoice}
          customPayoutAddress={customPayoutAddress}
          error={error}
          onBack={goBack}
          onCreate={handleCreateVault}
        />
      )}

      {step === 'creating' && (
        <div class="accept-loading" role="status" aria-live="polite">
          <div class="accept-spinner" aria-hidden="true" />
          <p>Creating your vault…</p>
          <p class="accept-loading-sub">
            Generating your keys and registering the vault — this takes a few seconds.
          </p>
        </div>
      )}

      {step === 'registering' && (
        <div class="accept-loading" role="status" aria-live="polite">
          <div class="accept-spinner" aria-hidden="true" />
          <p>Approve the vault policy on your Ledger…</p>
          <p class="accept-loading-sub">
            Your vault was created. Now review its spending policy on the
            device screen — it lists the vault&apos;s four keys, and your
            device will only ever co-sign withdrawals under the policy you
            approve here.
          </p>
        </div>
      )}

      {step === 'register-failed' && (
        <div class="accept-loading" role="alert">
          <p class="accept-error">{error}</p>
          <p class="accept-loading-sub">
            Your vault exists, but its policy is not registered on the device
            yet — this device cannot co-sign withdrawals for it until the
            registration completes.
          </p>
          <div class="accept-actions">
            <button
              class="btn btn-primary"
              onClick={() => { void retryRegistration(); }}
            >
              {needsReconnect ? 'Reconnect device and try again' : 'Try again'}
            </button>
          </div>
        </div>
      )}

      {step === 'success' && vaultResponse && (
        <SuccessStep
          vaultResponse={vaultResponse}
          principalSats={principalSats}
          userPayoutAddress={sealPayoutAddress}
          showFaucet={
            session.bitcoinNetwork !== null && session.bitcoinNetwork !== 'bitcoin'
          }
        />
      )}
    </div>
  );
}

// ── Step indicator ────────────────────────────────────────────────────

function StepIndicator({ current }: { current: number }) {
  const total = WIZARD_STEPS.length;
  return (
    <div class="accept-steps" aria-label={`Step ${current} of ${total}`}>
      {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
        <span
          key={n}
          class={
            n === current ? 'accept-step-dot active'
            : n < current ? 'accept-step-dot done'
            : 'accept-step-dot'
          }
        />
      ))}
    </div>
  );
}

// ── Step 1: amount ────────────────────────────────────────────────────

interface AmountStepProps {
  offer: OfferResponse;
  btcInput: string;
  setBtcInput: (v: string) => void;
  exceedsLimit: boolean;
  canSubmit: boolean;
  estimatedRewardSats: number;
  onBack: () => void;
  onNext: () => void;
}

function AmountStep({
  offer,
  btcInput,
  setBtcInput,
  exceedsLimit,
  canSubmit,
  estimatedRewardSats,
  onBack,
  onNext,
}: AmountStepProps) {
  const settleShort = formatMaturityShort(offer.maturity_date);
  return (
    <div>
      <ProductSubtitle productco={offer.productco} />
      <h2 class="accept-title">Choose your deposit</h2>
      <div class="accept-field">
        <input
          class="accept-amount-input tabular-nums"
          type="text"
          inputMode="decimal"
          placeholder="0.1"
          value={btcInput}
          onInput={(e) => setBtcInput((e.target as HTMLInputElement).value)}
          aria-label="Deposit amount in BTC"
        />
        <p class="accept-input-hint tabular-nums">
          Offer has {offer.offer_limit_btc.toFixed(4)} BTC capacity remaining.
        </p>
        {exceedsLimit && (
          <p class="accept-input-error">
            Amount exceeds the offer capacity. Choose up to{' '}
            <span class="tabular-nums">{offer.offer_limit_btc.toFixed(4)} BTC</span>.
          </p>
        )}
      </div>
      {canSubmit && estimatedRewardSats > 0 && (
        <p class="accept-reward-preview">
          You'll earn{' '}
          <strong class="tabular-nums">{formatBtc(estimatedRewardSats)} BTC</strong>{' '}
          over {offer.days_to_expiry} days. Settles {settleShort}.
        </p>
      )}
      <div class="accept-actions">
        <button type="button" class="btn btn-secondary" onClick={onBack}>Back</button>
        <button
          type="button"
          class="btn btn-primary"
          onClick={onNext}
          disabled={!canSubmit}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}

// ── Step 2: payout ────────────────────────────────────────────────────

interface PayoutStepProps {
  payoutChoice: PayoutChoice;
  setPayoutChoice: (c: PayoutChoice) => void;
  walletAddress: string | null;
  customPayoutAddress: string;
  setCustomPayoutAddress: (v: string) => void;
  canSubmit: boolean;
  onBack: () => void;
  onNext: () => void;
}

function PayoutStep({
  payoutChoice,
  setPayoutChoice,
  walletAddress,
  customPayoutAddress,
  setCustomPayoutAddress,
  canSubmit,
  onBack,
  onNext,
}: PayoutStepProps) {
  return (
    <div>
      <h2 class="accept-title">Where should payouts go?</h2>
      <div class="payout-options">
        <label
          class={`payout-option${payoutChoice === 'login-wallet' ? ' payout-option--selected' : ''}`}
        >
          <input
            type="radio"
            name="payout-choice"
            class="payout-option-radio"
            checked={payoutChoice === 'login-wallet'}
            onChange={() => setPayoutChoice('login-wallet')}
          />
          <div class="payout-option-body">
            <div class="payout-option-title">Your login wallet</div>
            <div class="payout-option-subtitle mono">
              {walletAddress ?? 'Loading wallet address…'}
            </div>
            <div class="payout-option-meta">The address you signed in with.</div>
          </div>
        </label>
        <label
          class={`payout-option${payoutChoice === 'custom' ? ' payout-option--selected' : ''}`}
        >
          <input
            type="radio"
            name="payout-choice"
            class="payout-option-radio"
            checked={payoutChoice === 'custom'}
            onChange={() => setPayoutChoice('custom')}
          />
          <div class="payout-option-body">
            <div class="payout-option-title">Send somewhere else</div>
            <input
              class="accept-input mono payout-option-input"
              type="text"
              placeholder="bc1…"
              value={customPayoutAddress}
              onInput={(e) => {
                setCustomPayoutAddress((e.target as HTMLInputElement).value);
                if (payoutChoice !== 'custom') setPayoutChoice('custom');
              }}
              onFocus={() => setPayoutChoice('custom')}
            />
          </div>
        </label>
      </div>

      <details class="accept-why">
        <summary>Why default to your login wallet?</summary>
        <p>
          Cobault settles to the address derived from your sign-in
          method, so your BTC lands where the Wallet page can already
          spend it. Override only if you want to route payouts directly
          to cold storage or another address you control.
        </p>
      </details>

      <div class="accept-actions">
        <button type="button" class="btn btn-secondary" onClick={onBack}>Back</button>
        <button
          type="button"
          class="btn btn-primary"
          onClick={onNext}
          disabled={!canSubmit}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}

// ── Step 3: confirm ───────────────────────────────────────────────────

interface ConfirmStepProps {
  offer: OfferResponse;
  principalSats: number;
  estimatedRewardSats: number;
  payoutChoice: PayoutChoice;
  customPayoutAddress: string;
  error: string | null;
  onBack: () => void;
  onCreate: () => void;
}

function ConfirmStep({
  offer,
  principalSats,
  estimatedRewardSats,
  payoutChoice,
  customPayoutAddress,
  error,
  onBack,
  onCreate,
}: ConfirmStepProps) {
  const maxLossSats = Math.round(principalSats * offer.economics.max_loss_pct);
  const settleLong = `${offer.maturity_date}, 08:00 UTC`;
  // COB-394: when a descriptor is present the agreement panel reads as an
  // agreement with the branded counterparty. Inert today (absent → no
  // lockup rendered, DOM identical to pre-COB-394).
  const brand = resolveBrand(offer.productco);
  const customTrimmed = customPayoutAddress.trim();
  const payoutLabel = payoutChoice === 'custom' && customTrimmed.length > 0
    ? truncate(customTrimmed, 24)
    : 'Your login wallet';

  return (
    <div>
      <p class="accept-eyebrow">
        Fixed reward · {formatPercent(offer.economics.yield_apy)} ·{' '}
        {offer.days_to_expiry}-day term · paid at settlement
      </p>
      <h2 class="accept-title">Confirm</h2>
      <dl class="confirm-card">
        <div class="confirm-row">
          <dt>Deposit</dt>
          <dd class="tabular-nums">{formatBtc(principalSats)} BTC</dd>
        </div>
        <div class="confirm-row">
          <dt>Reward</dt>
          <dd class="tabular-nums">
            {formatBtc(estimatedRewardSats)} BTC{' '}
            <span class="confirm-row-suffix">· fixed</span>
          </dd>
        </div>
        <div class="confirm-row">
          <dt>Term</dt>
          <dd class="tabular-nums">{offer.days_to_expiry} days</dd>
        </div>
        <div class="confirm-row">
          <dt>Settles</dt>
          <dd>{settleLong}</dd>
        </div>
        <div class="confirm-row">
          <dt>Payout address</dt>
          <dd class={payoutChoice === 'custom' ? 'mono' : undefined}>
            {payoutChoice !== 'custom' && (
              <span class="confirm-wallet-pill">Your wallet</span>
            )}
            {payoutLabel}
          </dd>
        </div>
      </dl>

      {/* COB-261: "What you're agreeing to" — the settlement risk restated
          as the three index-price outcomes, in the dark gold-tinted panel
          from the accept-confirm mockup. Same information the prose summary
          + disclosure carried; every figure is derived from the offer. */}
      <section class="accept-outcomes">
        <div class="accept-outcomes-head">
          <span class="accept-outcomes-diamonds" aria-hidden="true">◇◆◇</span>
          <h3 class="accept-outcomes-title">What you&rsquo;re agreeing to</h3>
        </div>
        <BrandLockup brand={brand} class="accept-outcomes-lockup" />
        <p class="accept-outcomes-intro">
          At settlement on {formatMaturityShort(offer.maturity_date)}, the BTC
          index price decides one of three outcomes. There&rsquo;s nothing more
          to do once the vault is open.
        </p>

        <div class="accept-outcome">
          <div class="accept-outcome-cond">
            <span class="accept-outcome-pill accept-outcome-pill--best">
              Best / unchanged
            </span>
            <span>
              BTC settles at or below{' '}
              <strong class="tabular-nums">
                {formatUsd(offer.short_leg.strike)}
              </strong>
            </span>
          </div>
          <p class="accept-outcome-detail">
            Your <strong>full {formatBtc(principalSats)} BTC</strong> is
            returned, <strong>plus</strong> the {formatBtc(estimatedRewardSats)}{' '}
            BTC reward.
          </p>
        </div>

        <div class="accept-outcome">
          <div class="accept-outcome-cond">
            <span class="accept-outcome-pill accept-outcome-pill--risk">
              Capital at risk
            </span>
            <span>
              BTC settles above{' '}
              <strong class="tabular-nums">
                {formatUsd(offer.short_leg.strike)}
              </strong>
            </span>
          </div>
          <p class="accept-outcome-detail">
            A portion of your deposit is forfeit —{' '}
            <strong>up to {formatPercent(offer.economics.max_loss_pct, 0)}</strong>{' '}
            (max <span class="tabular-nums">{formatBtc(maxLossSats)} BTC</span>).
            You keep the rest, plus the reward.
          </p>
        </div>

        <div class="accept-outcome">
          <div class="accept-outcome-cond">
            <span class="accept-outcome-pill accept-outcome-pill--cap">
              Beyond the cap
            </span>
            <span>
              Any increase past{' '}
              <strong class="tabular-nums">
                {formatUsd(offer.long_leg.strike)}
              </strong>
            </span>
          </div>
          <p class="accept-outcome-detail">
            Is yours to keep — the forfeit never exceeds{' '}
            <strong>{formatPercent(offer.economics.max_loss_pct, 0)}</strong>.
          </p>
        </div>

        <details class="accept-risk-full">
          <summary>Read full risk disclosure</summary>
          <p>
            This vault implements a bear-call-spread payout. Your deposit is
            locked for {offer.days_to_expiry} days. At maturity ({offer.maturity_date},
            08:00 UTC), the BTC index settlement price determines how much
            of your deposit is returned and how much funds the platform's
            option position:
          </p>
          <ul>
            <li>
              BTC ≤ {formatUsd(offer.short_leg.strike)}: you keep your full
              deposit plus the {formatBtc(estimatedRewardSats)} BTC reward.
            </li>
            <li>
              {formatUsd(offer.short_leg.strike)} &lt; BTC &lt;{' '}
              {formatUsd(offer.long_leg.strike)}: a portion of your deposit
              is forfeit, proportional to how far the price exceeds the cap.
              You still receive the reward in full.
            </li>
            <li>
              BTC ≥ {formatUsd(offer.long_leg.strike)}: forfeiture is capped
              at {formatBtc(maxLossSats)} BTC ({formatPercent(offer.economics.max_loss_pct, 0)}).
              Any further BTC rally accrues to the BTC you keep — the cap
              does not grow with price.
            </li>
          </ul>
          <p>
            Settlement uses the Deribit BTC index at 08:00 UTC on the
            maturity date ({offer.maturity_date}). The{' '}
            {formatBtc(estimatedRewardSats)} BTC reward (
            {formatPercent(offer.economics.yield_apy)} annualised) is paid
            out regardless of the settlement outcome.
          </p>
        </details>
      </section>

      {error && <p class="accept-error">{error}</p>}

      <div class="accept-actions">
        <button type="button" class="btn btn-secondary" onClick={onBack}>
          <span aria-hidden="true">←</span> Back
        </button>
        <button type="button" class="btn btn-primary" onClick={onCreate}>
          Create vault <span aria-hidden="true">→</span>
        </button>
      </div>
      <p class="accept-cosign-note">
        You&rsquo;ll co-sign in your browser — your key never leaves this device.
      </p>
    </div>
  );
}

// ── Success step ──────────────────────────────────────────────────────

interface SuccessStepProps {
  vaultResponse: CreateVaultResponse;
  principalSats: number;
  /** COB-462: the payout address this browser chose for the vault, sealed
   *  into the clear-sign anchor once the contract is acknowledged. */
  userPayoutAddress: string | null;
  showFaucet: boolean;
}

/** Exported for tests: the acknowledgement seal has to be driven through the
 *  real modal wiring, or dropping the call again would go unnoticed exactly
 *  the way COB-245 dropped the create-time one. */
export function SuccessStep({
  vaultResponse,
  principalSats,
  userPayoutAddress,
  showFaucet,
}: SuccessStepProps) {
  const session = useSession();
  const { route } = useLocation();
  const address = vaultResponse.deposit_address;
  // COB-263: verifier-served confirm embed, launched in-place from the
  // creation success step. `verifierOrigin` is null on unknown networks —
  // then only the "Go to vault details" route is offered.
  const [showVerifierConfirm, setShowVerifierConfirm] = useState(false);
  const verifierOrigin = verifierOriginForNetwork(session.bitcoinNetwork);
  const explorerUrl = address
    ? mempoolAddressUrlForNetwork(session.bitcoinNetwork, address)
    : '';
  const [showQr, setShowQr] = useState(false);
  const [copied, setCopied] = useState(false);
  // COB-168: QR of the raw bech32 address in byte mode — the encoding
  // wallet apps expect. Error-correction 'L' (most compact) is fine here:
  // the address text above is the source of truth if the QR is damaged.
  const qrSvg = useMemo(() => {
    if (!showQr || !address) return '';
    const qr = qrcode(0, 'L');
    qr.addData(address, 'Byte');
    qr.make();
    return qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
  }, [address, showQr]);

  function handleCopy() {
    if (!address) return;
    navigator.clipboard.writeText(address).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  /** COB-462: the acknowledgement is the seal moment for the COB-178
   *  clear-sign anchor — the first point at which the browser can hold all
   *  three destinations (the deposit address is revealed only once the owner
   *  has confirmed the contract on the independent verifier; COB-245). Seal
   *  BEFORE routing so the locally-chosen payout address is what gets pinned:
   *  the vault page's repair pass would otherwise win the race with the
   *  server's echo of the same address. Best-effort throughout — the
   *  navigation happens whatever the seal did. */
  async function handleAcknowledged(): Promise<void> {
    setShowVerifierConfirm(false);
    try {
      await sealVaultAnchorAfterAcknowledgement({
        agent: session.agent,
        vaultId: vaultResponse.vault_id,
        userPayoutAddress,
        platformRefundAddress: vaultResponse.contract.platform_refund_address,
        getVaultDetails: api.getVaultDetails,
        onDepositAddressRevealed: (depositAddress) =>
          rememberVaultDepositAddress(vaultResponse.vault_id, depositAddress),
      });
    } finally {
      route(`/vault/${vaultResponse.vault_id}`);
    }
  }

  return (
    <div class="accept-success">
      <h2 class="accept-title">Vault created</h2>
      {address ? (
        <>
          <p class="accept-subtitle">
            Send exactly the deposit amount to the address below. The on-chain watcher will detect
            your deposit and activate the vault automatically.
          </p>
          <div class="accept-deposit-card">
            <div>
              <div class="accept-deposit-label">Deposit address</div>
              <div class="accept-deposit-address mono">
                {explorerUrl ? (
                  <a href={explorerUrl} target="_blank" rel="noopener">
                    {address}
                  </a>
                ) : (
                  address
                )}
              </div>
              {/* COB-168: copy + QR right here so the user can grab the
                  deposit address without first opening vault details. */}
              <div class="accept-deposit-actions">
                <button
                  type="button"
                  class="btn btn-link"
                  onClick={handleCopy}
                  aria-live="polite"
                >
                  {copied ? 'Copied' : 'Copy address'}
                </button>
                <button
                  type="button"
                  class="btn btn-link"
                  onClick={() => setShowQr((v) => !v)}
                  aria-expanded={showQr}
                >
                  {showQr ? 'Hide QR code' : 'Show QR code'}
                </button>
              </div>
              {showQr && (
                <div
                  class="accept-qr"
                  // QR svg is generated from a trusted on-chain-address string —
                  // no user input, no XSS vector.
                  dangerouslySetInnerHTML={{ __html: qrSvg }}
                />
              )}
            </div>
            <div class="accept-deposit-amount">
              <div class="accept-deposit-label">Amount to deposit</div>
              <div class="accept-deposit-address tabular-nums">
                {formatBtc(principalSats)} BTC
              </div>
            </div>
          </div>
          {showFaucet && (
            <FundVaultButton depositAddress={address} amountSats={principalSats} />
          )}
        </>
      ) : (
        // COB-245: the deposit address is withheld until the owner confirms
        // the registered contract on the independent verifier. COB-263: offer
        // to confirm in-place via the verifier-served embed; the direct route
        // to vault details (which also reveals the address once confirmed) is
        // retained below. COB-701: `btn-block` — the button is block-level and
        // full width like the vault-detail action card's CTA, so it takes its
        // own line above the link and the subtitle's margin is the only gap.
        <>
          <p class="accept-subtitle">
            Before you can fund this vault, confirm the registered contract on the independent
            verifier. Your deposit address is revealed only after you confirm — this protects you
            from funding a vault whose terms were altered.
          </p>
          {verifierOrigin && (
            <button
              type="button"
              class="btn btn-primary btn-block"
              onClick={() => setShowVerifierConfirm(true)}
            >
              Confirm your contract
            </button>
          )}
        </>
      )}
      <a href={`/vault/${vaultResponse.vault_id}`} class="accept-vault-link">
        Go to vault details
      </a>

      {/* COB-263: verifier-served confirm embed. On the advisory `confirmed`
          we seal the clear-sign anchor (COB-462) and route to the vault,
          where the now-revealed deposit address shows (the reveal is
          server-gated on the verifier's ack-status). */}
      {showVerifierConfirm && verifierOrigin && (
        <VerifierConfirmModal
          vaultId={vaultResponse.vault_id}
          verifierOrigin={verifierOrigin}
          onClose={() => setShowVerifierConfirm(false)}
          onConfirmed={() => { void handleAcknowledged(); }}
        />
      )}
    </div>
  );
}

// ── F6: One-click test sBTC funding for the new vault ─────────────────

function FundVaultButton({
  depositAddress,
  amountSats,
}: {
  depositAddress: string;
  amountSats: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [txid, setTxid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleClick() {
    setState('sending');
    setError(null);
    try {
      const resp = await api.requestFaucet({
        address: depositAddress,
        amount_sats: amountSats,
      });
      setTxid(resp.txid);
      setState('sent');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Faucet request failed.');
      setState('error');
    }
  }

  return (
    <div class="accept-testing-section">
      <button
        type="button"
        class="button-reset accept-testing-toggle"
        onClick={() => setExpanded(!expanded)}
      >
        Testing only {expanded ? '▾' : '▸'}
      </button>
      {expanded && state === 'sent' && txid && (
        <div class="accept-faucet-success">
          Funded with test sBTC. tx <span class="mono">{txid}</span>
          <p class="accept-faucet-note">
            This was sent from a test faucet, not your wallet. It will not appear in your wallet transactions.
          </p>
        </div>
      )}
      {expanded && !(state === 'sent' && txid) && (
        <div class="accept-faucet">
          <button
            class="btn btn-primary"
            onClick={handleClick}
            disabled={state === 'sending'}
          >
            {state === 'sending' ? 'Funding...' : 'Fund this vault with test sBTC'}
          </button>
          {error && <p class="accept-error">{error}</p>}
        </div>
      )}
    </div>
  );
}
